import React, { useMemo, useState, useEffect } from 'react';
import type { Question } from '../../types';
import { QuestionAnimationPlayer } from './QuestionAnimationPlayer';
import { AnswerOptions } from './PracticeTest.components';
import { HotspotQuestion } from './HotspotQuestion';
import { YesNoQuestion } from './YesNoQuestion';
import { FillinQuestion } from './FillinQuestion';
import { QuestionImageOrVideo } from './QuestionImageOrVideo';
import '../PracticeTest.css';

interface ProfessionalQuestionDisplayProps {
  currentQuestion?: Question | null;
  questionNumber?: number;
  selectedAnswer?: string;
  isAnswered?: boolean;
  onAnswer?: (answerId: string) => void;
  streakMessage?: string | null;
}

/**
 * Professional CBR Exam Layout Component
 *
 * Renders questions in CBR exam format:
 * - Media-first layout: image/video FIRST → question → options
 * - Text-only layout: question → options (compact, no gaps)
 *
 * Responsive across all devices:
 * - Mobile: <375px, 375-480px, 480-768px
 * - Tablet: 768px-1024px
 * - Desktop: 1024px+
 */
export const ProfessionalQuestionDisplay: React.FC<ProfessionalQuestionDisplayProps> = ({
  currentQuestion,
  questionNumber = 1,
  selectedAnswer,
  isAnswered,
  onAnswer,
  streakMessage,
}) => {
  // Detect media type
  const hasAnimation = useMemo(
    () => !!(currentQuestion as { animationId?: string | null } | undefined)?.animationId,
    [currentQuestion]
  );

  const hasImage = useMemo(
    () => !!(currentQuestion as { imageUrl?: string } | undefined)?.imageUrl,
    [currentQuestion]
  );

  const isMediaFirst = hasAnimation || hasImage;

  const [animationFailed, setAnimationFailed] = useState(false);
  useEffect(() => setAnimationFailed(false), [currentQuestion?.id]);

  if (!currentQuestion) {
    return null;
  }

  const displayText =
    currentQuestion.text ||
    (currentQuestion as { question?: string }).question ||
    '';

  // Hotspot layout: Large sign image + visual tap tiles (no letter prefix)
  if (currentQuestion.questionType === 'hotspot' && !hasAnimation) {
    return (
      <HotspotQuestion
        question={currentQuestion}
        questionNumber={questionNumber}
        selectedAnswer={selectedAnswer}
        isAnswered={isAnswered}
        onAnswer={onAnswer}
        streakMessage={streakMessage}
      />
    );
  }

  // Yes/No layout: Binary decision — two large YES/NO buttons
  if (currentQuestion.questionType === 'yesno' && currentQuestion.yesNoMapping) {
    return (
      <YesNoQuestion
        question={currentQuestion}
        questionNumber={questionNumber}
        selectedAnswer={selectedAnswer}
        isAnswered={isAnswered}
        onAnswer={onAnswer}
        streakMessage={streakMessage}
      />
    );
  }

  // Fill-in layout: text input with check button
  if (currentQuestion.questionType === 'fillin') {
    return (
      <FillinQuestion
        question={currentQuestion}
        questionNumber={questionNumber}
        selectedAnswer={selectedAnswer}
        isAnswered={isAnswered}
        onAnswer={onAnswer}
        streakMessage={streakMessage}
      />
    );
  }

  // Media-first layout: Animation/Image FIRST, then question, then options
  if (isMediaFirst) {
    return (
      <div className="professional-question-display media-first">
        {/* Animation (priority) */}
        {hasAnimation && !animationFailed && (
          <QuestionAnimationPlayer
            animationId={(currentQuestion as { animationId?: string | null } | undefined)?.animationId}
            onVideoError={() => setAnimationFailed(true)}
          />
        )}

        {/* Image: shown when there's no animation, or animation failed to load */}
        {hasImage && (!hasAnimation || animationFailed) && currentQuestion.imageUrl && (
          <div className="practice-question-image">
            <QuestionImageOrVideo url={currentQuestion.imageUrl} alt="question visual" />
          </div>
        )}

        {/* Question text */}
        <div className="professional-question-text">
          <span className="professional-question-number">{questionNumber})</span>{' '}
          {displayText}
        </div>

        {/* Answer options */}
        <div className="practice-options-group">
          <AnswerOptions
            question={currentQuestion}
            selectedAnswer={selectedAnswer}
            isAnswered={isAnswered}
            onAnswer={onAnswer}
          />
        </div>

        {/* Streak message (optional) */}
        {streakMessage && (
          <div className="practice-streak-message">
            {streakMessage}
          </div>
        )}
      </div>
    );
  }

  // Text-only layout: Compact (no gaps), question → options
  return (
    <div className="professional-question-display text-only">
      {/* Question text (no top gap) */}
      <div className="professional-question-text">
        <span className="professional-question-number">{questionNumber})</span>{' '}
        {displayText}
      </div>

      {/* Answer options */}
      <div className="practice-options-group">
        <AnswerOptions
          question={currentQuestion}
          selectedAnswer={selectedAnswer}
          isAnswered={isAnswered}
          onAnswer={onAnswer}
        />
      </div>

      {/* Streak message (optional) */}
      {streakMessage && (
        <div className="practice-streak-message">
          {streakMessage}
        </div>
      )}
    </div>
  );
};

ProfessionalQuestionDisplay.displayName = 'ProfessionalQuestionDisplay';
